import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import RunDetail from "./RunDetail";

const fmtQar = (v) => `QAR ${Number(v || 0).toLocaleString()}`;

export const STATUS_CHIP = {
  draft: "border border-slate-400 text-slate-600",
  reviewed: "bg-amber-500 text-white",
  approved: "bg-teal-600 text-white",
  paid: "bg-[var(--brand-primary)] text-white",
};

export const RunsPanel = () => {
  const [runs, setRuns] = useState(null);
  const [selected, setSelected] = useState(null);
  const [period, setPeriod] = useState("");
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = () => api.get("/payroll/runs").then((r) => setRuns(r.data.runs));
  useEffect(() => { load(); }, []);

  const createRun = async () => {
    setCreating(true);
    try {
      const r = await api.post("/payroll/runs", { period });
      toast.success(`Draft run created for ${r.data.period}`);
      setOpen(false);
      setPeriod("");
      await load();
      setSelected(r.data.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  if (selected) return <RunDetail runId={selected} onBack={() => { setSelected(null); load(); }} />;
  if (!runs) return <Skeleton className="h-80 w-full rounded-none" />;

  return (
    <div className="space-y-4" data-testid="runs-panel">
      <div className="flex items-center justify-between">
        <p className="text-[12px] text-muted-foreground">
          Lifecycle: draft → reviewed → approved → paid. Approval is restricted to Administrators.
        </p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-run-btn" className="rounded-none bg-[var(--brand-primary)] hover:bg-[#0f172a] h-9 text-[12px]">
              <Plus className="h-4 w-4" /> New Payroll Run
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none sm:max-w-sm">
            <DialogHeader><DialogTitle className="text-[15px]">Create draft payroll run</DialogTitle></DialogHeader>
            <div className="space-y-2">
              <p className="text-[12px] text-muted-foreground">Period (YYYY-MM)</p>
              <Input data-testid="run-period-input" value={period} onChange={(e) => setPeriod(e.target.value)}
                placeholder="2026-08" className="rounded-none" />
            </div>
            <DialogFooter>
              <Button data-testid="run-create-submit" onClick={createRun} disabled={creating || !period}
                className="rounded-none bg-teal-600 hover:bg-teal-700">
                {creating ? "Computing…" : "Create draft"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-border">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
              {["Period", "Status", "Headcount", "Gross", "Incentives", "Net", "Exceptions", "Created by", ""].map((h, i) => (
                <th key={i} className="px-4 py-2.5 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {runs.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50 cursor-pointer" data-testid={`run-row-${r.period}`}
                onClick={() => setSelected(r.id)}>
                <td className="px-4 py-3 font-mono font-semibold text-[var(--brand-primary)]">{r.period}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] font-mono px-2 py-1 uppercase ${STATUS_CHIP[r.status]}`}>{r.status}</span>
                </td>
                <td className="px-4 py-3">{r.totals.headcount}</td>
                <td className="px-4 py-3 font-mono">{fmtQar(r.totals.gross)}</td>
                <td className="px-4 py-3 font-mono text-teal-700">{fmtQar(r.totals.incentives)}</td>
                <td className="px-4 py-3 font-mono font-semibold">{fmtQar(r.totals.net)}</td>
                <td className="px-4 py-3">
                  {r.exceptions_count > 0
                    ? <span className="text-[10px] font-mono bg-amber-50 border border-amber-300 text-amber-800 px-1.5 py-0.5">{r.exceptions_count} FLAGGED</span>
                    : <span className="text-[11px] text-slate-400">none</span>}
                </td>
                <td className="px-4 py-3 text-[12px] text-slate-500">{r.created_by}</td>
                <td className="px-4 py-3 text-right text-[11px] text-teal-700 font-medium">Open →</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RunsPanel;
